/**
 * Helpers for driving HTMLVideoElement playback without leaking unhandled
 * promise rejections.
 *
 * `video.play()` returns a promise that rejects when playback cannot start.
 * The two rejections we see in the wild are:
 *  - `AbortError`  - a `pause()` or a new load request landed while the play
 *                    request was still pending (scroll-driven autoplay, HLS
 *                    `attachMedia`, carousel slide changes).
 *  - `NotSupportedError` - the element has no usable source yet.
 *
 * Discarding the promise with `void` does NOT mark it as handled, so every one
 * of those rejections surfaces as a global `unhandledrejection`.
 */

/** Tracks the in-flight play request per element so pause() can wait for it. */
const pendingPlayRequests = new WeakMap<HTMLVideoElement, Promise<void>>();

/**
 * The most recent intent expressed for an element.
 *
 * `play()` flips `video.paused` to false synchronously but its promise settles
 * much later, so a pause deferred behind it can outlive the intent that queued
 * it. Re-reading this after the await lets a newer `play()` cancel a stale
 * pause instead of leaving an on-screen video frozen.
 */
const desiredPlaybackState = new WeakMap<HTMLVideoElement, 'play' | 'pause'>();

const EXPECTED_PLAYBACK_ERRORS = new Set(['AbortError', 'NotSupportedError', 'NotAllowedError']);

/**
 * Returns true when the element has a source the browser can attempt to load.
 * Calling `play()` without one rejects with `NotSupportedError`.
 */
export function hasPlayableSource(video: HTMLVideoElement | null | undefined): boolean {
	if (!video) {
		return false;
	}

	// The browser already resolved a source (covers hls.js MediaSource blobs).
	if (video.currentSrc) {
		return true;
	}

	// `src=""` resolves to the document URL, which is never a valid media source.
	if (video.getAttribute('src')) {
		return true;
	}

	return Array.from(video.querySelectorAll('source')).some((source) => !!source.getAttribute('src'));
}

/**
 * Starts playback, swallowing the rejections that are expected during normal
 * scroll/interaction races. Unexpected errors are re-surfaced on the console so
 * they remain debuggable.
 */
export function safePlayVideo(video: HTMLVideoElement | null | undefined): Promise<void> {
	if (!video || !hasPlayableSource(video)) {
		return Promise.resolve();
	}

	desiredPlaybackState.set(video, 'play');

	// Already playing - a second play() would be a no-op but still allocates a
	// promise that can be aborted by the next pause(). Hand back the in-flight
	// request so callers still await the real settle point.
	if (!video.paused && !video.ended) {
		return pendingPlayRequests.get(video) ?? Promise.resolve();
	}

	try {
		const playRequest: Promise<void> | undefined = video.play();

		// Pre-2016 browsers return undefined from play().
		if (playRequest === undefined) {
			return Promise.resolve();
		}

		const trackedRequest: Promise<void> = playRequest.catch(reportPlaybackError).finally(() => {
			if (pendingPlayRequests.get(video) === trackedRequest) {
				pendingPlayRequests.delete(video);
			}
		});

		pendingPlayRequests.set(video, trackedRequest);

		return trackedRequest;
	} catch (error) {
		// Some older browsers throw synchronously instead of rejecting.
		reportPlaybackError(error);
		return Promise.resolve();
	}
}

/**
 * Pauses the element, deferring until any in-flight play request settles so the
 * browser never reports "The play() request was interrupted by a call to
 * pause()".
 *
 * The deferred pause is dropped if a newer `safePlayVideo` call supersedes it
 * while the play request is still pending - otherwise a fast scroll out and
 * back in would settle with the video paused on screen.
 */
export function safePauseVideo(video: HTMLVideoElement | null | undefined): Promise<void> {
	if (!video) {
		return Promise.resolve();
	}

	desiredPlaybackState.set(video, 'pause');

	const pendingPlay = pendingPlayRequests.get(video);

	if (pendingPlay === undefined) {
		pauseIfPlaying(video);
		return Promise.resolve();
	}

	return pendingPlay.then(() => {
		if (desiredPlaybackState.get(video) === 'pause') {
			pauseIfPlaying(video);
		}
	});
}

function pauseIfPlaying(video: HTMLVideoElement) {
	if (!video.paused) {
		video.pause();
	}
}

interface FullscreenCapableElement extends HTMLElement {
	webkitRequestFullscreen?: () => Promise<void> | void;
	mozRequestFullScreen?: () => Promise<void> | void;
	msRequestFullscreen?: () => Promise<void> | void;
	webkitEnterFullscreen?: () => Promise<void> | void;
}

/**
 * Requests fullscreen using the first available vendor API.
 *
 * Every one of these returns a promise that rejects when the browser refuses
 * the request (`AbortError: The operation was aborted.`) or when the element
 * cannot be presented fullscreen (`NotSupportedError: The operation is not
 * supported.`), which is routine on iOS and in cross-origin iframes.
 */
export function safeRequestFullscreen(
	element: FullscreenCapableElement | null | undefined,
	{ preferWebkitEnterFullscreen = false } = {}
): Promise<void> {
	if (!element) {
		return Promise.resolve();
	}

	const candidates = [
		preferWebkitEnterFullscreen ? element.webkitEnterFullscreen : undefined,
		element.requestFullscreen,
		element.webkitRequestFullscreen,
		element.mozRequestFullScreen,
		element.msRequestFullscreen,
		element.webkitEnterFullscreen,
	];

	const request = candidates.find(Boolean)?.bind(element);

	if (!request) {
		return Promise.resolve();
	}

	try {
		return Promise.resolve(request()).catch(reportPlaybackError);
	} catch (error) {
		reportPlaybackError(error);
		return Promise.resolve();
	}
}

function reportPlaybackError(error: unknown) {
	const name = (error as { name?: string })?.name;

	if (name && EXPECTED_PLAYBACK_ERRORS.has(name)) {
		return;
	}

	console.warn('Video playback failed', error);
}
